import React, { useState, useEffect } from 'react';
import { Plus, Users, Mail, Settings, AlertTriangle, Save, Clock, Send, TrendingUp, FileText, Calendar } from 'lucide-react';
import { UserGroup, NotificationRule } from '../types';
import api from '../services/api';

const UserGroupManagement: React.FC = () => {
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [notificationRules, setNotificationRules] = useState<NotificationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddGroupForm, setShowAddGroupForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [editingRule, setEditingRule] = useState<number | null>(null);
  const [editEmails, setEditEmails] = useState('');
  const [exams, setExams] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedRule, setSelectedRule] = useState<any>(null);
  const [showTrackingConfig, setShowTrackingConfig] = useState(false);
  const [trackingConfig, setTrackingConfig] = useState<{
    trackingItems: Array<{ type: string; id: number; name: string; item: any }>;
    reportFrequency: string;
    reportDayOfWeek: number;
    recipientEmails: string[];
  }>({
    trackingItems: [],
    reportFrequency: 'weekly',
    reportDayOfWeek: 1,
    recipientEmails: []
  });
  const [configuringRule, setConfiguringRule] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [groupsResponse, rulesResponse, examsResponse, coursesResponse] = await Promise.all([
        api.get('/admin/user-groups'),
        api.get('/admin/notification-rules'),
        api.get('/admin/exams'),
        api.get('/admin/courses')
      ]);
      
      setUserGroups(groupsResponse.data);
      setNotificationRules(rulesResponse.data);
      setExams(examsResponse.data);
      setCourses(coursesResponse.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch data');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigureTracking = async (rule: any) => {
    try {
      setConfiguringRule(rule.id);
      setSelectedRule(rule);
      
      // Fetch existing tracking configuration for this rule
      const trackingResponse = await api.get(`/email-reports/groups/${rule.user_group}/tracking`);
      const existingRules = trackingResponse.data;
      const existingRule = existingRules.find((r: any) => r.id === rule.id);
      
      // Initialize tracking config with existing data or defaults
      setTrackingConfig({
        trackingItems: existingRule?.trackingItems?.map((item: any) => ({
          type: item.item_type,
          id: item.item_id,
          name: item.exam?.name || item.course?.title || 'Unknown Item',
          item: item.exam || item.course
        })) || [],
        reportFrequency: rule.report_frequency || 'weekly',
        reportDayOfWeek: rule.report_day_of_week || 1,
        recipientEmails: rule.recipient_emails || []
      });
      
      setShowTrackingConfig(true);
    } catch (error: any) {
      setError('Error loading tracking configuration: ' + (error.response?.data?.message || error.message));
    } finally {
      setConfiguringRule(null);
    }
  };

  const handleAddTrackingItem = (type: string, item: any) => {
    const newItem = {
      type,
      id: item.id,
      name: type === 'exam' ? item.name : item.title,
      item
    };
    
    setTrackingConfig(prev => ({
      ...prev,
      trackingItems: [...prev.trackingItems.filter(ti => !(ti.type === type && ti.id === item.id)), newItem]
    }));
  };

  const handleRemoveTrackingItem = (type: string, id: number) => {
    setTrackingConfig(prev => ({
      ...prev,
      trackingItems: prev.trackingItems.filter(item => !(item.type === type && item.id === id))
    }));
  };

  const handleSaveTrackingConfig = async () => {
    try {
      // Save tracking configuration
      const trackingResponse = await api.put(`/email-reports/groups/${selectedRule.user_group}/tracking`, {
        ruleId: selectedRule.id,
        trackingItems: trackingConfig.trackingItems,
        reportFrequency: trackingConfig.reportFrequency,
        reportDayOfWeek: trackingConfig.reportDayOfWeek
      });

      // Update emails separately
      const emailResponse = await api.put(`/admin/notification-rules/${selectedRule.id}`, {
        recipient_emails: trackingConfig.recipientEmails
      });

      setShowTrackingConfig(false);
      fetchData(); // Refresh data
    } catch (error: any) {
      setError('Error saving configuration: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSendTestReport = async (groupName: string) => {
    try {
      const response = await api.post(`/email-reports/groups/${groupName}/send-report`, {
        reportType: 'weekly'
      });
      alert('Test report sent successfully!');
    } catch (error: any) {
      setError('Error sending test report: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newGroupName.trim()) {
      setError('Group name is required');
      return;
    }

    try {
      setError(null); // Clear any previous errors
      const response = await api.post('/admin/user-groups', { groupName: newGroupName.trim() });
      console.log('Group creation response:', response.data);
      setNewGroupName('');
      setShowAddGroupForm(false);
      fetchData(); // Refresh data
    } catch (error: any) {
      console.error('Group creation error:', error);
      setError(error.response?.data?.message || `Failed to create group: ${error.message}`);
    }
  };

  const handleUpdateNotificationRule = async (ruleId: number) => {
    try {
      const emails = editEmails.split(',').map(email => email.trim()).filter(email => email);
      
      await api.put(`/admin/notification-rules/${ruleId}`, {
        recipient_emails: emails
      });
      
      setEditingRule(null);
      setEditEmails('');
      fetchData(); // Refresh data
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update notification rule');
    }
  };

  const handleToggleRuleActive = async (ruleId: number, isActive: boolean) => {
    try {
      await api.put(`/admin/notification-rules/${ruleId}`, {
        is_active: !isActive
      });
      fetchData(); // Refresh data
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to toggle notification rule');
    }
  };

  const startEditingRule = (rule: NotificationRule) => {
    setEditingRule(rule.id);
    setEditEmails(rule.recipient_emails.join(', '));
  };

  const cancelEditingRule = () => {
    setEditingRule(null);
    setEditEmails('');
  };

  const groupedRules = userGroups.reduce((acc, group) => {
    acc[group.name] = notificationRules.filter(rule => rule.user_group === group.name);
    return acc;
  }, {} as Record<string, NotificationRule[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-white flex items-center">
          <Users className="mr-2 h-6 w-6" />
          User Groups & Notifications
        </h2>
        <button
          onClick={() => setShowAddGroupForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition duration-300 flex items-center"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Group
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-100 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {showAddGroupForm && (
        <div className="mb-6 p-4 bg-gray-700 rounded-lg border border-gray-600">
          <h3 className="text-lg font-medium text-white mb-3">Create New User Group</h3>
          <form onSubmit={handleCreateGroup} className="flex space-x-3">
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Enter group name (e.g., 'finance', 'hr', 'managers')"
              className="flex-1 p-2 bg-gray-600 border border-gray-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              required
            />
            <button
              type="submit"
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-500 transition"
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddGroupForm(false);
                setNewGroupName('');
              }}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500 transition"
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      <div className="space-y-6">
        {userGroups.map((group) => (
          <div key={group.name} className="bg-gray-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <Users className="mr-2 h-5 w-5" />
                  {group.name} Group
                </h3>
                <span className="ml-2 px-2 py-1 text-xs bg-blue-500/20 text-blue-300 rounded-full">
                  {groupedRules[group.name]?.length || 0} notification rule{groupedRules[group.name]?.length !== 1 ? 's' : ''}
                </span>
              </div>
              <button
                onClick={() => handleSendTestReport(group.name)}
                className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-500 transition flex items-center text-sm"
              >
                <Send className="h-4 w-4 mr-1" />
                Send Test Report
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {groupedRules[group.name]?.map((rule) => (
                <div key={rule.id} className="bg-gray-600 rounded-md p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-blue-400" />
                      <span className="text-white font-medium capitalize">
                        {rule.notification_type.replace('_', ' ')} Reports
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        rule.is_active 
                          ? 'bg-green-500/20 text-green-300' 
                          : 'bg-red-500/20 text-red-300'
                      }`}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => startEditingRule(rule)}
                        className="text-blue-400 hover:text-blue-300 transition"
                        title="Edit emails"
                      >
                        <Settings className="h-4 w-4" />
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await handleConfigureTracking(rule);
                          } catch (error) {
                            console.error('Error configuring tracking:', error);
                          }
                        }}
                        disabled={configuringRule === rule.id}
                        className={`transition ${
                          configuringRule === rule.id 
                            ? 'text-gray-500 cursor-not-allowed' 
                            : 'text-green-400 hover:text-green-300'
                        }`}
                        title="Configure tracking"
                      >
                        {configuringRule === rule.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-green-400"></div>
                        ) : (
                          <TrendingUp className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleToggleRuleActive(rule.id, rule.is_active)}
                        className={`${
                          rule.is_active ? 'text-red-400 hover:text-red-300' : 'text-green-400 hover:text-green-300'
                        } transition`}
                        title={rule.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {rule.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </div>

                  {editingRule === rule.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editEmails}
                        onChange={(e) => setEditEmails(e.target.value)}
                        placeholder="Enter email addresses separated by commas"
                        className="w-full p-2 bg-gray-700 border border-gray-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 resize-none h-20"
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleUpdateNotificationRule(rule.id)}
                          className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-500 transition flex items-center text-sm"
                        >
                          <Save className="h-3 w-3 mr-1" />
                          Save
                        </button>
                        <button
                          onClick={cancelEditingRule}
                          className="bg-gray-600 text-white px-3 py-1 rounded-md hover:bg-gray-500 transition text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm">
                      <div className="text-gray-300">
                        <strong>Recipients:</strong> {rule.recipient_emails?.length || 0} email(s)
                      </div>
                      <div className="text-gray-300">
                        <strong>Frequency:</strong> {rule.report_frequency || 'immediate'}
                      </div>
                      <div className="text-gray-300">
                        <strong>Tracking:</strong> {rule.trackingItems?.length || 0} item(s)
                      </div>
                      {rule.last_report_sent && (
                        <div className="text-gray-400 text-xs">
                          Last sent: {new Date(rule.last_report_sent).toLocaleString()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {userGroups.length === 0 && (
        <div className="text-center py-8 text-gray-400">
          <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No user groups found. Create your first group to get started.</p>
        </div>
      )}

      {/* Tracking Configuration Modal */}
      {showTrackingConfig && selectedRule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">
                  Configure {selectedRule.user_group} Group - {selectedRule.notification_type.replace('_', ' ')} Reports
                </h3>
                <button
                  onClick={() => setShowTrackingConfig(false)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Email Recipients */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email Recipients
                  </label>
                  <textarea
                    value={trackingConfig.recipientEmails.join(', ')}
                    onChange={(e) => setTrackingConfig(prev => ({
                      ...prev,
                      recipientEmails: e.target.value.split(',').map(email => email.trim()).filter(email => email)
                    }))}
                    placeholder="Enter email addresses separated by commas"
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    rows={3}
                  />
                </div>

                {/* Report Frequency */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Report Frequency
                    </label>
                    <select
                      value={trackingConfig.reportFrequency}
                      onChange={(e) => setTrackingConfig(prev => ({
                        ...prev,
                        reportFrequency: e.target.value
                      }))}
                      className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:border-blue-500"
                    >
                      <option value="immediate">Immediate</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  {trackingConfig.reportFrequency === 'weekly' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Day of Week
                      </label>
                      <select
                        value={trackingConfig.reportDayOfWeek}
                        onChange={(e) => setTrackingConfig(prev => ({
                          ...prev,
                          reportDayOfWeek: parseInt(e.target.value)
                        }))}
                        className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:border-blue-500"
                      >
                        <option value={1}>Monday</option>
                        <option value={2}>Tuesday</option>
                        <option value={3}>Wednesday</option>
                        <option value={4}>Thursday</option>
                        <option value={5}>Friday</option>
                        <option value={6}>Saturday</option>
                        <option value={0}>Sunday</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Tracking Items */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Items to Track
                  </label>
                  
                  {/* Current tracking items */}
                  {trackingConfig.trackingItems.length > 0 && (
                    <div className="mb-4 space-y-2">
                      {trackingConfig.trackingItems.map((item) => (
                        <div key={`${item.type}-${item.id}`} className="flex items-center justify-between bg-gray-700 p-3 rounded-md">
                          <div className="flex items-center space-x-2">
                            {item.type === 'exam' ? <FileText className="h-4 w-4 text-blue-400" /> : <TrendingUp className="h-4 w-4 text-green-400" />}
                            <span className="text-white">{item.name}</span>
                            <span className="text-xs text-gray-400 uppercase">{item.type}</span>
                          </div>
                          <button
                            onClick={() => handleRemoveTrackingItem(item.type, item.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add new items */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-300 mb-2">Available Exams</h4>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {exams.map((exam) => (
                          <button
                            key={exam.id}
                            onClick={() => handleAddTrackingItem('exam', exam)}
                            className={`w-full text-left p-2 rounded text-sm transition ${
                              trackingConfig.trackingItems.some(item => item.type === 'exam' && item.id === exam.id)
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            <FileText className="inline h-3 w-3 mr-1" />
                            {exam.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-300 mb-2">Available Courses</h4>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {courses.map((course) => (
                          <button
                            key={course.id}
                            onClick={() => handleAddTrackingItem('course', course)}
                            className={`w-full text-left p-2 rounded text-sm transition ${
                              trackingConfig.trackingItems.some(item => item.type === 'course' && item.id === course.id)
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            <TrendingUp className="inline h-3 w-3 mr-1" />
                            {course.title}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex space-x-3 pt-4 border-t border-gray-600">
                  <button
                    onClick={handleSaveTrackingConfig}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-500 transition flex items-center"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Save Configuration
                  </button>
                  <button
                    onClick={() => setShowTrackingConfig(false)}
                    className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-500 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserGroupManagement; 